/**
 * Shared combobox keyboard navigation utility functions
 * used by both Elements and React packages.
 * Only framework-agnostic functions belong here.
 */

/**
 * Scrolls an element into view and focuses it.
 */
export const focusAndScrollIntoView = (element: HTMLElement): void => {
  element.scrollIntoView({ block: 'nearest' })
  window.setTimeout(() => element.focus(), 0)
}

/**
 * Gets all selectable option elements from a container.
 */
export const getOptionElements = (container: HTMLElement): HTMLElement[] => {
  return Array.from(
    container.querySelectorAll('[role="option"]:not([data-disabled])') || [],
  ) as HTMLElement[]
}

/**
 * Focuses the next sibling option.
 */
export const focusNextOption = (currentTarget: HTMLElement): void => {
  const nextOption = currentTarget.nextElementSibling as HTMLElement
  if (nextOption) {
    focusAndScrollIntoView(nextOption)
  }
}

/**
 * Focuses the previous sibling option.
 * Falls back to search input if at first option and search is present.
 */
export const focusPreviousOption = (
  currentTarget: HTMLElement,
  container: HTMLElement,
  includeSearch: boolean,
): void => {
  const previousOption = currentTarget.previousElementSibling as HTMLElement
  if (currentTarget.dataset.index === '0' && includeSearch) {
    const searchInput = container.querySelector('[role="searchbox"]') as HTMLElement
    if (searchInput) {
      focusAndScrollIntoView(searchInput)
    }
  } else if (previousOption) {
    focusAndScrollIntoView(previousOption)
  }
}

/**
 * Focuses the first option in the list.
 */
export const focusFirstOption = (container: HTMLElement): void => {
  const options = getOptionElements(container)
  if (options[0]) {
    focusAndScrollIntoView(options[0])
  }
}

/**
 * Focuses the last option in the list.
 */
export const focusLastOption = (container: HTMLElement): void => {
  const options = getOptionElements(container)
  const lastOption = options[options.length - 1]
  if (lastOption) {
    focusAndScrollIntoView(lastOption)
  }
}

/**
 * Focuses the first selected option, or falls back to search/new-option/first option.
 */
export const focusFirstOrSelectedOption = (
  container: HTMLElement,
  config: {
    disabled: boolean
    allowUserInput: boolean
    customUserInput: string | null
    includeSearch: boolean
  },
): void => {
  if (config.disabled) return

  const options = getOptionElements(container)

  if (config.allowUserInput && config.customUserInput) {
    const newOption = container.querySelector('[data-type="new-option"]') as HTMLElement
    if (newOption) {
      focusAndScrollIntoView(newOption)
      return
    }
  }

  const selectedOption = options.find((option) => option.dataset.selected === 'true')
  if (selectedOption) {
    focusAndScrollIntoView(selectedOption)
    return
  }

  if (config.includeSearch && !(document.activeElement instanceof HTMLInputElement)) {
    const searchInput = container.querySelector('[role="searchbox"]') as HTMLElement
    if (searchInput) {
      window.setTimeout(() => searchInput.focus(), 0)
      return
    }
  }

  if (options[0]) {
    focusAndScrollIntoView(options[0])
  }
}
